import { displayMessage } from '@/helpers/helper'
import { i18n } from '@/i18n'
import axios from 'axios'

/**
 * @typedef {Object} CatalogSettings
 * @property {string} url - The base URL for the catalog API
 * @property {string} default_store - The default store identifier
 * @property {boolean} enabled - Whether the catalog feature is enabled
 */

/**
 * @typedef {Object} AuthProvider
 * @property {string} accessToken - The bearer token used for API authentication
 * @property {number} expiresAt - Token expiration timestamp in milliseconds
 * @property {Function} isAuthenticated - Method to refresh authentication status
 */

/**
 * @typedef {Object} FormattedImage
 * @property {string} size
 * @property {string} url
 */

/**
 * @typedef {Object} FormattedOption
 * @property {boolean} displayed
 * @property {boolean} is_tag
 * @property {string} label
 * @property {string} value
 * @property {string[]} values
 */

/**
 * @typedef {Object} FormattedPrice
 * @property {string} currency
 * @property {string} [date_end]
 * @property {string} [date_start]
 * @property {number} [discount_percent]
 * @property {string} [discount_type]
 * @property {boolean} has_discount
 * @property {string} [location_filter]
 * @property {number} price
 * @property {number} [price_no_discount]
 * @property {number} [purchase_price]
 * @property {number} [tax_deee]
 * @property {number} [tax_mob]
 * @property {number} [tax_percent]
 * @property {boolean} with_tax
 */

/**
 * @typedef {Object} FormattedStock
 * @property {number} quantity
 * @property {number} [quantity_defective]
 * @property {number} [quantity_in_order]
 * @property {number} [quantity_in_transit]
 * @property {number} [quantity_order]
 * @property {number} [quantity_other]
 * @property {number} [quantity_reserved]
 * @property {number} [quantity_to_return]
 * @property {number} [quantity_total]
 * @property {string} location_id
 */

/**
 * @typedef {Object} Dimensions
 * @property {number} length - Length in mm
 * @property {number} width - Width in mm
 * @property {number} height - Height in mm
 */

/**
 * @typedef {Object} RelatedProduct
 * @property {string} product_id - Product identifier
 * @property {string} relation_option_label - Label of the option used to relate products
 * @property {string} relation_option_value - Value of the option used to relate products
 */

/**
 * @typedef {Object} FormattedVariant
 * @property {string} [barcode]
 * @property {Array<{type: string, barcode: string}>} [other_barcodes]
 * @property {string} [color_hexa]
 * @property {string} [color_image]
 * @property {string} id
 * @property {FormattedImage[]} [images]
 * @property {string} [long_description]
 * @property {string} [name]
 * @property {Object.<string, FormattedOption>} [options]
 * @property {FormattedPrice[]} [prices]
 * @property {string} [short_description]
 * @property {string} [sku]
 * @property {FormattedStock[]} [stocks]
 * @property {Dimensions} [dimensions]
 * @property {number} [weight] - Weight in grams
 */

/**
 * @typedef {Object} FormattedProduct
 * @property {string} id
 * @property {string} [sku]
 * @property {string} [name]
 * @property {FormattedImage[]} [images]
 * @property {RelatedProduct[]} [related_products]
 * @property {Object.<string, FormattedOption>} [options]
 * @property {Object.<string, {quantity: number}>} [total_quantity] - Total quantity by location id
 * @property {FormattedVariant[]} [variants]
 */

/**
 * @typedef {Object} FormattedVariantAutocomplete
 * @property {string} [barcode]
 * @property {string} id
 * @property {string} [images]
 * @property {string} [name]
 * @property {FormattedPrice} [price]
 * @property {string} [sku]
 */

/**
 * @typedef {Object} FormattedProductAutocomplete
 * @property {string} id
 * @property {string} [sku]
 * @property {string} [images]
 * @property {string} [name]
 * @property {FormattedVariantAutocomplete[]} [variants]
 */

/**
 * @typedef {Object} APIError
 * @property {string} message - Error message
 * @property {number} status - HTTP status code
 * @property {string} [code] - Error code
 */

/**
 * Service for interacting with the Octipas Catalog API.
 * Provides methods for product retrieval, search autocomplete, and API authentication management.
 *
 * @class OctipasCatalogService
 * @example
 * const catalogService = new OctipasCatalogService(catalogSettings, authProvider);
 * const product = await catalogService.getProductById('prod123');
 */
export class OctipasCatalogService {
  /**
   * Authentication provider instance
   * @type {AuthProvider}
   */
  auth

  /**
   * Current access token for API requests
   * @type {string}
   */
  accessToken

  /**
   * Promise tracking ongoing token refresh operation
   * @type {Promise<void>|null}
   */
  tokenRefreshing

  /**
   * Default store identifier used in API requests
   * @type {string}
   */
  defaultStore

  /**
   * Axios instance configured for catalog API requests
   * @type {import('axios').AxiosInstance}
   */
  catalogAPI

  /**
   * Creates a new instance of the OctipasCatalogService.
   *
   * Initializes the service with the provided catalog settings and authentication provider.
   * Sets up an Axios instance with request/response interceptors for automatic token refresh
   * and error handling.
   *
   * @constructor
   * @param {CatalogSettings} catalogSetting - The catalog configuration settings
   * @param {AuthProvider} auth - The authentication provider containing the access token
   * @throws {Error} Throws an error if the catalog configuration is missing, disabled, or incomplete
   *
   * @example
   * const catalogSettings = {
   *   url: 'https://api.catalog.com',
   *   default_store: 'store123',
   *   enabled: true
   * };
   * const authProvider = {
   *   accessToken: 'bearer_token',
   *   expiresAt: Date.now() + 3600000,
   *   isAuthenticated: async () => { ... }
   * };
   * const service = new OctipasCatalogService(catalogSettings, authProvider);
   */
  constructor(catalogSetting, auth) {
    this.auth = auth
    this.accessToken = auth?.accessToken
    this.tokenRefreshing = null
    this.defaultStore = catalogSetting?.default_store

    // Ensure we have the settings
    if (!catalogSetting?.enabled) {
      this.emitError('access_denied', true)
    }

    if (!catalogSetting?.url || !catalogSetting?.default_store) {
      this.emitError('missing_configuration', true)
    }

    // Initialize the Axios HTTP client with interceptors
    this.catalogAPI = axios.create({
      baseURL: catalogSetting.url,
      timeout: 29900,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${this.accessToken}`,
      },
    })

    // Request interceptor for automatic token refresh
    this.catalogAPI.interceptors.request.use(
      (config) => this.refreshTokenIfNeeded(config),
      (error) => Promise.reject(error)
    )

    // Response interceptor for centralized error handling
    this.catalogAPI.interceptors.response.use(
      (response) => response,
      (error) => {
        this.emitError('api_error')
        console.error('Catalog API Error:', error)
        return Promise.reject(error)
      }
    )
  }

  /**
   * Refreshes the access token if it's expired or about to expire before making API requests.
   * Prevents concurrent token refresh operations by using a shared promise.
   *
   * @private
   * @param {import('axios').AxiosRequestConfig} config - Axios request configuration object
   * @returns {Promise<import('axios').AxiosRequestConfig>} Updated configuration with fresh token
   *
   * @example
   * // This method is called automatically by the request interceptor
   * // No direct usage required
   */
  async refreshTokenIfNeeded(config) {
    const now = Date.now()

    // Check if token is still valid (with 30 second buffer before token expires)
    if (this.auth.expiresAt - 30000 > now) {
      return config
    }

    // Prevent concurrent token refreshes
    if (!this.tokenRefreshing) {
      this.tokenRefreshing = this.refreshToken()
    }

    await this.tokenRefreshing
    config.headers.Authorization = `Bearer ${this.accessToken}`
    return config
  }

  /**
   * Refreshes the authentication token by calling the auth provider's authentication method.
   * Updates the access token and Axios default headers with the new token.
   *
   * @private
   * @returns {Promise<void>} Promise that resolves when token refresh is complete
   * @throws {Error} Throws if token refresh fails
   *
   * @example
   * // This method is called automatically when token expires
   * // No direct usage required
   */
  async refreshToken() {
    try {
      await this.auth.isAuthenticated()
      this.accessToken = this.auth.accessToken
      this.catalogAPI.defaults.headers.Authorization = `Bearer ${this.accessToken}`
    } finally {
      this.tokenRefreshing = null
    }
  }

  /**
   * Displays error messages through the application's message system and optionally throws an error.
   * Uses internationalization for localized error messages.
   *
   * @private
   * @param {string} messageKey - Translation key for the error message in the catalog namespace
   * @param {boolean} [withThrow=false] - Whether to throw an Error after displaying the message
   * @throws {Error} Throws an error with the localized message if withThrow is true
   *
   * @example
   * // Display error message only
   * this.emitError('api_error');
   *
   * // Display error message and throw exception
   * this.emitError('missing_configuration', true);
   */
  emitError(messageKey, withThrow = false) {
    const text = i18n.te(`catalog.${messageKey}`) ? i18n.t(`catalog.${messageKey}`) : messageKey
    displayMessage(text)
    if (withThrow) {
      throw new Error(text)
    }
  }

  /**
   * Makes a GET request to the catalog API with automatic parameter cleaning.
   * Removes null and undefined parameters before sending the request.
   *
   * @private
   * @param {string} path - API endpoint path relative to the base URL
   * @param {Object} [params={}] - Query parameters object
   * @returns {Promise<any>} Promise resolving to the API response data
   * @throws {Error} Throws if the API request fails
   *
   * @example
   * const data = await this.get('/products', { limit: 10, category: 'electronics' });
   */
  async get(path, params = {}) {
    // Remove undefined/null parameters
    const cleanedParams = Object.fromEntries(
      // eslint-disable-next-line no-unused-vars
      Object.entries(params).filter(([_key, value]) => ![null, undefined].includes(value))
    )
    const response = await this.catalogAPI.get(path, { params: cleanedParams })
    return response.data
  }

  /**
   * Retrieves a single product by its unique identifier.
   * Supports localization and location-based filtering.
   *
   * @param {string} productId - Unique product identifier (required, non-empty)
   * @param {string|null} [lang=null] - Language code for localized content (e.g., 'en', 'fr')
   * @param {string|string[]|null} [locationIds=null] - Location identifier(s). Uses default store if not provided
   * @returns {Promise<FormattedProduct>} Promise resolving to the formatted product object
   * @throws {Error} Throws if productId is null, undefined, empty, or whitespace-only
   *
   * @example
   * // Get product with default settings
   * const product = await service.getProductById('prod123');
   *
   * // Get localized product for specific location
   * const product = await service.getProductById('prod123', 'en', 'store1');
   *
   * // Get product for multiple locations
   * const product = await service.getProductById('prod123', 'fr', ['store1', 'store2']);
   */
  async getProductById(productId, lang = null, locationIds = null) {
    if ([null, undefined, ''].includes(productId?.trim())) {
      this.emitError('product_id_required', true)
    }

    const params = {
      location_id: locationIds ?? this.defaultStore,
      keep_product_without_price: 1,
      lang,
    }

    return this.get(`/products/${encodeURIComponent(productId)}`, params)
  }

  /**
   * Gets autocomplete suggestions for product search queries.
   * Provides real-time search suggestions as users type their search terms.
   *
   * @param {string} query - Search query string (required, must be a non-empty string)
   * @param {string|null} [lang] - Language code for localized suggestions
   * @param {string|null} [locationId] - Location identifier for location-specific results. Uses default store if not provided
   * @param {string|null} [priceLocationFilter=null] - Price location filter (by default uses location_id)
   * @param {number} [keepProductWithoutPrice=1] - Keep products without price in results (1 for true)
   * @returns {Promise<FormattedProductAutocomplete[]>} Promise resolving to array of autocomplete product objects
   * @throws {Error} Throws if query is not a string type
   *
   * @example
   * // Basic autocomplete
   * const suggestions = await service.autocompleteProducts('laptop');
   *
   * // Localized autocomplete for specific location
   * const suggestions = await service.autocompleteProducts('ordinateur', 'fr', 'store1');
   *
   * // Exclude products without prices
   * const suggestions = await service.autocompleteProducts('phone', 'en', 'store1', null, 0);
   */
  async autocompleteProducts(query, lang, locationId, priceLocationFilter = null, keepProductWithoutPrice = 1) {
    if (typeof query !== 'string') {
      this.emitError('non_empty_query', true)
    }

    return this.get('/autocomplete/products', {
      query,
      lang,
      location_id: locationId ?? this.defaultStore,
      price_location_filter: priceLocationFilter,
      keep_product_without_price: keepProductWithoutPrice,
    })
  }
}
